import { Building, Database, FormInput, Mail, MessageSquare, Table } from 'lucide-react';
import type { CustomNodeProps, DataSchema } from '../../types/nodes';
import { BaseNode } from './BaseNode';
import { DatabaseConfig } from './DatabaseConfig';
import { EmailConfig } from './EmailConfig';
import { GoogleSheetsConfig } from './GoogleSheetsConfig';
import { HubSpotConfig } from './HubSpotConfig';
import { SlackConfig } from './SlackConfig';
import { SubmissionFormConfig } from './SubmissionFormConfig';

const formOutputSchema: Record<string, DataSchema> = {
	formData: {
		type: 'object',
		description: 'The submitted form data',
		properties: {
			fields: {
				type: 'object',
				description: 'Form field values',
			},
			metadata: {
				type: 'object',
				description: 'Form submission metadata',
				properties: {
					submittedAt: {
						type: 'string',
						description: 'Timestamp of submission',
					},
					ipAddress: {
						type: 'string',
						description: 'Submitter IP address',
					},
				},
			},
		},
	},
};

const databaseOutputSchema: Record<string, DataSchema> = {
	result: {
		type: 'object',
		description: 'Database operation result',
		properties: {
			data: {
				type: 'array',
				description: 'Query results or affected records',
				items: {
					type: 'object',
					description: 'Database record',
				},
			},
			metadata: {
				type: 'object',
				description: 'Operation metadata',
				properties: {
					affectedRows: {
						type: 'number',
						description: 'Number of affected records for write operations',
					},
					lastInsertId: {
						type: 'string',
						description: 'ID of the last inserted record',
					},
					executionTime: {
						type: 'number',
						description: 'Operation execution time in milliseconds',
					},
				},
			},
		},
	},
};

const emailOutputSchema: Record<string, DataSchema> = {
	result: {
		type: 'object',
		description: 'Email sending result',
		properties: {
			sent: {
				type: 'boolean',
				description: 'Whether the email was sent successfully',
			},
			messageId: {
				type: 'string',
				description: 'Unique message identifier',
			},
			timestamp: {
				type: 'string',
				description: 'When the email was sent',
			},
		},
	},
};

const slackOutputSchema: Record<string, DataSchema> = {
	result: {
		type: 'object',
		description: 'Slack message result',
		properties: {
			sent: {
				type: 'boolean',
				description: 'Whether the message was sent successfully',
			},
			messageId: {
				type: 'string',
				description: 'Slack message ID',
			},
			channelId: {
				type: 'string',
				description: 'Channel where message was sent',
			},
			timestamp: {
				type: 'string',
				description: 'Message timestamp',
			},
		},
	},
};

const hubspotOutputSchema: Record<string, DataSchema> = {
	result: {
		type: 'object',
		description: 'HubSpot operation result',
		properties: {
			hubspotCompanyId: {
				type: 'string',
				description: 'HubSpot company ID',
			},
			action: {
				type: 'string',
				description: 'Action performed (created or updated)',
			},
			companyData: {
				type: 'object',
				description: 'Company data from HubSpot',
			},
			createdAt: {
				type: 'string',
				description: 'When the company was created',
			},
			updatedAt: {
				type: 'string',
				description: 'When the company was last updated',
			},
		},
	},
};

const googleSheetsOutputSchema: Record<string, DataSchema> = {
	result: {
		type: 'object',
		description: 'Google Sheets write result',
		properties: {
			spreadsheetId: {
				type: 'string',
				description: 'The Google Sheets spreadsheet ID that was written to',
			},
			range: {
				type: 'string',
				description: 'The range in the spreadsheet that was written to',
			},
			rowsWritten: {
				type: 'number',
				description: 'Number of rows written to the spreadsheet',
			},
			columnsWritten: {
				type: 'number',
				description: 'Number of columns written to the spreadsheet',
			},
			appendMode: {
				type: 'boolean',
				description: 'Whether data was appended or overwritten',
			},
			success: {
				type: 'boolean',
				description: 'Whether the write operation was successful',
			},
			updatedRange: {
				type: 'string',
				description: 'The actual range that was updated',
			},
		},
	},
};

export function SubmissionFormNode(props: CustomNodeProps) {
	// Handle both legacy submission-form (integrations) and reclassified start.form (triggers)
	if (props.data.category !== 'integrations' && props.data.category !== 'triggers') return null;

	const isLegacy = props.data.category === 'integrations';
	const isTrigger = props.data.category === 'triggers';

	const nodeData = {
		...props.data,
		// Don't override the label - use the actual node data
		description: isTrigger ? 'Triggers workflow when form is submitted' : 'Collects form submissions',
		outputs: formOutputSchema,
	};

	return (
		<BaseNode
			{...props}
			data={nodeData}
			icon={FormInput}
			canHaveInputs={false} // Submission form is the root node - no inputs
			configComponent={SubmissionFormConfig as any}
		/>
	);
}

export function DatabaseNode(props: CustomNodeProps) {
	if (props.data.category !== 'integrations') return null;

	const nodeData = {
		...props.data,
		outputs: databaseOutputSchema,
	};

	return (
		<BaseNode
			{...props}
			data={nodeData}
			icon={Database}
			configComponent={DatabaseConfig as any} // TODO: Fix type properly in BaseNode
		/>
	);
}

export function EmailNode(props: CustomNodeProps) {
	if (props.data.category !== 'integrations') return null;

	const nodeData = {
		...props.data,
		outputs: emailOutputSchema,
	};

	return (
		<BaseNode
			{...props}
			data={nodeData}
			icon={Mail}
			configComponent={EmailConfig as any} // TODO: Fix type properly in BaseNode
		/>
	);
}

export function SlackNode(props: CustomNodeProps) {
	if (props.data.category !== 'integrations') return null;

	const nodeData = {
		...props.data,
		outputs: slackOutputSchema,
	};

	return (
		<BaseNode
			{...props}
			data={nodeData}
			icon={MessageSquare}
			configComponent={SlackConfig as any} // TODO: Fix type properly in BaseNode
		/>
	);
}

export function HubSpotNode(props: CustomNodeProps) {
	if (props.data.category !== 'integrations') return null;

	const nodeData = {
		...props.data,
		outputs: hubspotOutputSchema,
	};

	return (
		<BaseNode
			{...props}
			data={nodeData}
			icon={Building}
			configComponent={HubSpotConfig as any} // TODO: Fix type properly in BaseNode
		/>
	);
}

export function GoogleSheetsNode(props: CustomNodeProps) {
	if (props.data.category !== 'integrations') return null;

	const nodeData = {
		...props.data,
		outputs: googleSheetsOutputSchema,
	};

	return (
		<BaseNode
			{...props}
			data={nodeData}
			icon={Table}
			configComponent={GoogleSheetsConfig as any} // TODO: Fix type properly in BaseNode
		/>
	);
}
